import { Injectable } from '@angular/core';

interface ActiveLineItem {
  id: string;
  rate: number;
}

@Injectable({
  providedIn: 'root'
})
export class LaborRateService {
  private activeLineItems: Map<string, ActiveLineItem> = new Map();

  constructor() {
    console.log('LaborRateService: Service initialized, clearing any existing rates');
    this.activeLineItems.clear();
  }

  /**
   * Registers or updates a line item with its rate.
   * Only stores rates greater than 0.
   * @param lineItemId Unique identifier for the line item
   * @param rate The rate value to store
   */
  registerLineItem(lineItemId: string, rate: number): void {
    console.log('LaborRateService: Registering/updating line item:', lineItemId, 'with rate:', rate);
    if (this.isValidRate(rate)) {
      this.activeLineItems.set(lineItemId, { id: lineItemId, rate });
      console.log('LaborRateService: Line item registered. Active items:', this.getDebugInfo());
    } else {
      console.log('LaborRateService: Rate rejected (invalid):', rate);
    }
  }

  /**
   * Removes a line item from tracking.
   * @param lineItemId The line item identifier to remove
   */
  removeLineItem(lineItemId: string): void {
    console.log('LaborRateService: Removing line item:', lineItemId);
    const removed = this.activeLineItems.delete(lineItemId);
    console.log('LaborRateService: Line item removed:', removed, 'Remaining items:', this.getDebugInfo());
  }

  /**
   * Gets the most appropriate rate for new line items.
   * Returns the lowest rate from active line items, or null if no active items exist.
   * @returns The lowest rate from active items or null if no rates exist
   */
  getLowestRate(): number | null {
    if (this.activeLineItems.size === 0) {
      console.log('LaborRateService: No active line items, returning null');
      return null;
    }
    
    const rates = Array.from(this.activeLineItems.values()).map(item => item.rate);
    const lowestRate = Math.min(...rates);
    console.log('LaborRateService: Returning lowest rate:', lowestRate, 'from rates:', rates);
    return lowestRate;
  }

  /**
   * Checks if a rate is valid (greater than 0).
   * @param rate The rate to validate
   * @returns true if rate is valid, false otherwise
   */
  private isValidRate(rate: number): boolean {
    return rate != null && rate > 0;
  }

  /**
   * Clears all stored rates and active line items (useful for testing or session reset).
   */
  clearRates(): void {
    console.log('LaborRateService: Clearing all rates and active line items');
    this.activeLineItems.clear();
  }

  /**
   * Gets all active line items for debugging purposes.
   * @returns Array of all active line items with their rates
   */
  private getDebugInfo(): { id: string, rate: number }[] {
    return Array.from(this.activeLineItems.values());
  }

  /**
   * Gets all stored rates (for debugging purposes).
   * @returns Array of all stored rates
   */
  getAllRates(): number[] {
    return Array.from(this.activeLineItems.values()).map(item => item.rate).sort((a, b) => a - b);
  }

  /**
   * @deprecated Use registerLineItem instead
   */
  updateRate(rate: number): void {
    console.warn('LaborRateService: updateRate is deprecated. Use registerLineItem instead.');
  }
}